import React, { useState, useEffect, useMemo } from 'react';
import './AdminDashboard.css';

const TOURNAMENT_ID = 'AFBD5D83-4524-44D9-8D44-9FF0D39D9D2F';
const PAGE_SIZES = [5, 10, 25];

const MEAL_COLORS = {
  Breakfast: '#9AE3F0',
  Lunch:     '#48CAE4',
  'Hi-Tea':  '#00B4D8',
  Dinner:    '#0096C7',
};

const MEAL_DOT_CLASSES = {
  Breakfast: 'modern-dot-breakfast',
  Lunch:     'modern-dot-lunch',
  'Hi-Tea':  'modern-dot-hitea',
  Dinner:    'modern-dot-dinner',
};

/* ─── helpers ─────────────────────────────────────────────── */
const getInitials = (name) =>
  name
    .split(' ')
    .map((n) => n[0])
    .join('');

const formatDate = (d) => {
  // d is a Date object
  const yyyy = d.getFullYear();
  const mm   = String(d.getMonth() + 1).padStart(2, '0');
  const dd   = String(d.getDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
};

/* Build the next 5 days starting today */
const buildDayOptions = () => {
  const days = [];
  const today = new Date();
  for (let i = 0; i < 5; i++) {
    const d = new Date(today);
    d.setDate(today.getDate() + i);
    const val = formatDate(d);
    const label = d.toLocaleDateString('en-US', {
      month: 'long',
      day:   '2-digit',
      year:  'numeric',
    });
    days.push({ value: val, label });
  }
  return days;
};

/* ─── Component ───────────────────────────────────────────── */
const AdminDashboard = () => {
  const dayOptions = useMemo(() => buildDayOptions(), []);

  const [selectedDay, setSelectedDay] = useState(dayOptions[0].value);

  // API state
  const [mealDistributions, setMealDistributions] = useState([]);
  const [roleAnalytics,     setRoleAnalytics]     = useState([]);
  const [recentScans,       setRecentScans]        = useState([]);
  const [loading,           setLoading]            = useState(false);
  const [error,             setError]              = useState(null);

  // Table state
  const [searchQuery,  setSearchQuery]  = useState('');
  const [currentPage,  setCurrentPage]  = useState(1);
  const [pageSize,     setPageSize]     = useState(5);

  /* ── Fetch on day change ── */
  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      setError(null);
      try {
        const url = `https://api.asterickx.com/api/Dashboard/GetDashboardData?tournamentId=${TOURNAMENT_ID}&date=${selectedDay}`;
        const res  = await fetch(url);
        if (!res.ok) throw new Error(`Server error: ${res.status}`);
        const json = await res.json();
        setMealDistributions(json.mealDistributions ?? []);
        setRoleAnalytics(json.roleAnalytics ?? []);
        setRecentScans(json.recentScans ?? []);
      } catch (err) {
        setError(err.message || 'Failed to fetch dashboard data.');
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [selectedDay]);

  /* Reset pagination when search or day changes */
  useEffect(() => { setCurrentPage(1); }, [searchQuery, selectedDay]);

  /* ── Derived chart values ── */
  const totalMeals = mealDistributions.reduce((s, m) => s + m.totalCount, 0);
  const maxMeal    = Math.max(...mealDistributions.map((m) => m.totalCount), 1);

  const adminRole  = roleAnalytics.find((r) => r.roleType === 'Admin')  ?? { totalCount: 0, percentage: 0 };
  const playerRole = roleAnalytics.find((r) => r.roleType === 'Player') ?? { totalCount: 0, percentage: 0 };
  const totalScans = adminRole.totalCount + playerRole.totalCount;
  const maxQr      = Math.max(adminRole.totalCount, playerRole.totalCount, 1);

  /* ── Table filtering & pagination ── */
  const filteredScans = useMemo(() => {
    const q = searchQuery.toLowerCase();
    return recentScans.filter(
      (s) =>
        s.adminName.toLowerCase().includes(q) ||
        s.date.toLowerCase().includes(q) ||
        s.time.toLowerCase().includes(q)
    );
  }, [recentScans, searchQuery]);

  const totalPages   = Math.max(1, Math.ceil(filteredScans.length / pageSize));
  const pagedScans   = filteredScans.slice((currentPage - 1) * pageSize, currentPage * pageSize);
  const safePages    = Math.min(currentPage, totalPages);

  /* Clamp currentPage if filters reduce total */
  useEffect(() => {
    if (currentPage > totalPages) setCurrentPage(totalPages);
  }, [totalPages, currentPage]);

  /* ── Pagination range helper ── */
  const pageRange = useMemo(() => {
    const range = [];
    const delta = 2;
    const left  = Math.max(1, safePages - delta);
    const right = Math.min(totalPages, safePages + delta);
    for (let i = left; i <= right; i++) range.push(i);
    return range;
  }, [safePages, totalPages]);

  /* ─── Render ─────────────────────────────────────────────── */
  return (
    <>
      {/* Bootstrap CDN — inject once */}
      <link
        rel="stylesheet"
        href="https://cdn.jsdelivr.net/npm/bootstrap@5.3.2/dist/css/bootstrap.min.css"
      />

      <div className="modern-dashboard-wrapper">
        {/* Header */}
        <div className="modern-header-gradient">
          <div className="modern-header-bg-animation"></div>
          <div className="modern-header-content">
            <div className="modern-header-title-wrapper">
              <h1 className="modern-header-title">Admin Dashboard</h1>
            </div>
          </div>
        </div>

        <div className="modern-main-content">
          {/* Stats Cards */}
          <div className="modern-stats-grid">
            <div className="modern-stat-card modern-stat-card-pulse">
              <div className="modern-stat-glow modern-stat-glow-purple"></div>
              <div className="modern-stat-content">
                <div className="modern-stat-info">
                  <p className="modern-stat-label">Total Players</p>
                  <p className="modern-stat-number modern-stat-number-purple">560</p>
                </div>
                <div className="modern-stat-icon-box modern-icon-box-purple">
                  <div className="modern-icon-pulse"></div>
                  <svg className="modern-stat-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                      d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
                  </svg>
                </div>
              </div>
            </div>

            <div className="modern-stat-card modern-stat-card-pulse">
              <div className="modern-stat-glow modern-stat-glow-blue"></div>
              <div className="modern-stat-content">
                <div className="modern-stat-info">
                  <p className="modern-stat-label">Total Tournaments</p>
                  <p className="modern-stat-number modern-stat-number-blue">4</p>
                </div>
                <div className="modern-stat-icon-box modern-icon-box-blue">
                  <div className="modern-icon-pulse"></div>
                  <svg className="modern-stat-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                      d="M9 12l2 2 4-4M7.835 4.697a3.42 3.42 0 001.946-.806 3.42 3.42 0 014.438 0 3.42 3.42 0 001.946.806 3.42 3.42 0 013.138 3.138 3.42 3.42 0 00.806 1.946 3.42 3.42 0 010 4.438 3.42 3.42 0 00-.806 1.946 3.42 3.42 0 01-3.138 3.138 3.42 3.42 0 00-1.946.806 3.42 3.42 0 01-4.438 0 3.42 3.42 0 00-1.946-.806 3.42 3.42 0 01-3.138-3.138 3.42 3.42 0 00-.806-1.946 3.42 3.42 0 010-4.438 3.42 3.42 0 00.806-1.946 3.42 3.42 0 013.138-3.138z" />
                  </svg>
                </div>
              </div>
            </div>

            <div className="modern-stat-card modern-stat-card-pulse">
              <div className="modern-stat-glow modern-stat-glow-green"></div>
              <div className="modern-stat-content">
                <div className="modern-stat-info">
                  <p className="modern-stat-label">Active Tournaments</p>
                  <p className="modern-stat-number modern-stat-number-green">1</p>
                </div>
                <div className="modern-stat-icon-box modern-icon-box-green">
                  <div className="modern-icon-pulse"></div>
                  <svg className="modern-stat-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                  </svg>
                </div>
              </div>
            </div>

            <div className="modern-stat-card modern-stat-card-pulse">
              <div className="modern-stat-glow modern-stat-glow-orange"></div>
              <div className="modern-stat-content">
                <div className="modern-stat-info">
                  <p className="modern-stat-label">Completed Tournaments</p>
                  <p className="modern-stat-number modern-stat-number-orange">3</p>
                </div>
                <div className="modern-stat-icon-box modern-icon-box-orange">
                  <div className="modern-icon-pulse"></div>
                  <svg className="modern-stat-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                      d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
              </div>
            </div>
          </div>

          {/* Day Selector */}
          <div className="modern-day-selector">
            <div className="modern-selector-inner">
              <label htmlFor="modern-day-select" className="modern-day-label">
                <svg className="modern-label-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                    d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>
                Select Day:
              </label>
              <select
                id="modern-day-select"
                value={selectedDay}
                onChange={(e) => setSelectedDay(e.target.value)}
                className="modern-day-dropdown"
              >
                {dayOptions.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* Loading / Error banner */}
          {loading && (
            <div className="d-flex justify-content-center align-items-center py-4">
              <div className="spinner-border text-info me-3" role="status" />
              <span style={{ color: '#48CAE4', fontWeight: 500 }}>Loading dashboard data…</span>
            </div>
          )}
          {error && !loading && (
            <div className="alert alert-danger mx-2" role="alert">
              ⚠️ {error}
            </div>
          )}

          {/* Charts */}
          {!loading && !error && (
            <div className="modern-charts-grid">
              {/* Meal Distribution */}
              <div className="modern-chart-card">
                <div className="modern-chart-header">
                  <div className="modern-chart-icon-wrapper modern-chart-icon-meals">
                    <svg className="modern-chart-header-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                        d="M3 3h2l.4 2M7 13h10l4-8H5.4M7 13L5.4 5M7 13l-2.293 2.293c-.63.63-.184 1.707.707 1.707H17m0 0a2 2 0 100 4 2 2 0 000-4zm-8 2a2 2 0 11-4 0 2 2 0 014 0z" />
                    </svg>
                  </div>
                  <div>
                    <h3 className="modern-chart-title">Meal Distribution</h3>
                    <p className="modern-chart-subtitle">{totalMeals} total meals served</p>
                  </div>
                </div>

                {mealDistributions.length === 0 ? (
                  <p className="text-center text-muted py-4">No meal data for this day.</p>
                ) : (
                  <div className="modern-donut-chart-wrapper">
                    <div className="modern-donut-chart">
                      {mealDistributions.map((meal, index) => {
                        const color = MEAL_COLORS[meal.mealType] ?? '#90E0EF';
                        return (
                          <div
                            key={meal.mealType}
                            className={`modern-donut-segment modern-donut-${meal.mealType.toLowerCase().replace('-', '')}`}
                            style={{ animationDelay: `${index * 0.1}s` }}
                          >
                            <div className="modern-donut-value">{meal.totalCount}</div>
                            <div
                              className="modern-donut-bar"
                              style={{ height: `${(meal.totalCount / maxMeal) * 100}%`, backgroundColor: color }}
                            />
                          </div>
                        );
                      })}
                    </div>
                    <div className="modern-meal-legend">
                      {mealDistributions.map((meal) => (
                        <div key={meal.mealType} className="modern-legend-item">
                          <div
                            className={`modern-legend-dot ${MEAL_DOT_CLASSES[meal.mealType] ?? 'modern-dot-breakfast'}`}
                          />
                          <span className="modern-legend-label">{meal.mealType}</span>
                          <span className="modern-legend-value">{meal.percentage.toFixed(1)}%</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              {/* QR Scan Analytics */}
              <div className="modern-chart-card">
                <div className="modern-chart-header">
                  <div className="modern-chart-icon-wrapper modern-chart-icon-qr">
                    <svg className="modern-chart-header-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                        d="M12 4v1m6 11h2m-6 0h-2v4m0-11v3m0 0h.01M12 12h4.01M16 20h4M4 12h4m12 0h.01M5 8h2a1 1 0 001-1V5a1 1 0 00-1-1H5a1 1 0 00-1 1v2a1 1 0 001 1zm12 0h2a1 1 0 001-1V5a1 1 0 00-1-1h-2a1 1 0 00-1 1v2a1 1 0 001 1zM5 20h2a1 1 0 001-1v-2a1 1 0 00-1-1H5a1 1 0 00-1 1v2a1 1 0 001 1z" />
                    </svg>
                  </div>
                  <div>
                    <h3 className="modern-chart-title">QR Scan Analytics</h3>
                    <p className="modern-chart-subtitle">{totalScans} total scans</p>
                  </div>
                </div>

                {roleAnalytics.length === 0 ? (
                  <p className="text-center text-muted py-4">No scan data for this day.</p>
                ) : (
                  <div className="modern-progress-chart">
                    {/* Admin */}
                    <div className="modern-progress-item">
                      <div className="modern-progress-header">
                        <div className="modern-progress-label-box">
                          <div className="modern-progress-icon modern-progress-icon-admin">
                            <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                                d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                            </svg>
                          </div>
                          <span className="modern-progress-label">Admin Scans</span>
                        </div>
                        <span className="modern-progress-value">{adminRole.totalCount}</span>
                      </div>
                      <div className="modern-progress-bar-container">
                        <div
                          className="modern-progress-bar modern-progress-bar-admin"
                          style={{ width: `${(adminRole.totalCount / maxQr) * 100}%` }}
                        >
                          <div className="modern-progress-shimmer" />
                        </div>
                      </div>
                      <div className="modern-progress-percentage">
                        {adminRole.percentage.toFixed(1)}% of total
                      </div>
                    </div>

                    {/* Player */}
                    <div className="modern-progress-item">
                      <div className="modern-progress-header">
                        <div className="modern-progress-label-box">
                          <div className="modern-progress-icon modern-progress-icon-player">
                            <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                                d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                            </svg>
                          </div>
                          <span className="modern-progress-label">Player Scans</span>
                        </div>
                        <span className="modern-progress-value">{playerRole.totalCount}</span>
                      </div>
                      <div className="modern-progress-bar-container">
                        <div
                          className="modern-progress-bar modern-progress-bar-player"
                          style={{ width: `${(playerRole.totalCount / maxQr) * 100}%` }}
                        >
                          <div className="modern-progress-shimmer" />
                        </div>
                      </div>
                      <div className="modern-progress-percentage">
                        {playerRole.percentage.toFixed(1)}% of total
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* ── Recent Scan Activity ─────────────────────────────── */}
          {!loading && !error && (
            <div className="modern-history-section">
              <div className="modern-history-header">
                <h3 className="modern-history-title">Recent Scan Activity</h3>
              </div>

              {/* Controls row */}
              <div className="d-flex flex-wrap align-items-center justify-content-between gap-2 mb-3 px-1">
                {/* Search */}
                <div style={{ position: 'relative', minWidth: '220px', flex: '1 1 220px', maxWidth: '360px' }}>
                  <span style={{
                    position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)',
                    color: '#48CAE4', pointerEvents: 'none',
                  }}>
                    <svg width="16" height="16" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                        d="M21 21l-4.35-4.35M17 11A6 6 0 115 11a6 6 0 0112 0z" />
                    </svg>
                  </span>
                  <input
                    type="text"
                    className="form-control form-control-sm"
                    placeholder="Search name, date, time…"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    style={{
                      paddingLeft: '36px',
                      background: 'rgba(255,255,255,0.06)',
                      border: '1px solid rgba(72,202,228,0.3)',
                      color: 'inherit',
                      borderRadius: '8px',
                    }}
                  />
                </div>

                {/* Page size */}
                <div className="d-flex align-items-center gap-2">
                  <label className="modern-day-label mb-0" style={{ fontSize: '13px', whiteSpace: 'nowrap' }}>
                    Rows:
                  </label>
                  <select
                    className="form-select form-select-sm"
                    value={pageSize}
                    onChange={(e) => { setPageSize(Number(e.target.value)); setCurrentPage(1); }}
                    style={{
                      width: '72px',
                      background: 'rgba(255,255,255,0.06)',
                      border: '1px solid rgba(72,202,228,0.3)',
                      color: 'inherit',
                      borderRadius: '8px',
                    }}
                  >
                    {PAGE_SIZES.map((s) => (
                      <option key={s} value={s}>{s}</option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Bootstrap Table */}
              <div className="modern-table-wrapper" style={{ overflowX: 'auto' }}>
                <table className="table table-hover table-bordered align-middle mb-0 modern-scan-table">
                  <thead>
                    <tr>
                      <th style={{ width: '48px' }}>#</th>
                      <th>Admin Name</th>
                      <th>Date</th>
                      <th>Time</th>
                    </tr>
                  </thead>
                  <tbody>
                    {pagedScans.length === 0 ? (
                      <tr>
                        <td colSpan={4} className="text-center py-4" style={{ color: '#aaa' }}>
                          {searchQuery ? 'No results match your search.' : 'No scan records for this day.'}
                        </td>
                      </tr>
                    ) : (
                      pagedScans.map((scan, index) => (
                        <tr key={index} style={{ animationDelay: `${index * 0.04}s` }}>
                          <td style={{ color: '#aaa', fontSize: '13px' }}>
                            {(currentPage - 1) * pageSize + index + 1}
                          </td>
                          <td>
                            <div className="modern-admin-cell">
                              <div className="modern-admin-avatar">
                                {getInitials(scan.adminName)}
                              </div>
                              <span className="modern-admin-name">{scan.adminName}</span>
                            </div>
                          </td>
                          <td className="modern-table-date">{scan.date}</td>
                          <td className="modern-table-time">{scan.time}</td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>

              {/* Pagination footer */}
              {filteredScans.length > 0 && (
                <div className="d-flex flex-wrap align-items-center justify-content-between gap-2 mt-3 px-1">
                  <span style={{ fontSize: '13px', color: '#aaa' }}>
                    Showing{' '}
                    <strong style={{ color: '#48CAE4' }}>
                      {Math.min((currentPage - 1) * pageSize + 1, filteredScans.length)}–
                      {Math.min(currentPage * pageSize, filteredScans.length)}
                    </strong>{' '}
                    of <strong style={{ color: '#48CAE4' }}>{filteredScans.length}</strong> records
                  </span>

                  <nav>
                    <ul className="pagination pagination-sm mb-0" style={{ gap: '4px' }}>
                      {/* Prev */}
                      <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                        <button
                          className="page-link"
                          onClick={() => setCurrentPage((p) => p - 1)}
                          style={pageBtnStyle}
                        >
                          ‹
                        </button>
                      </li>

                      {pageRange[0] > 1 && (
                        <>
                          <li className="page-item">
                            <button className="page-link" onClick={() => setCurrentPage(1)} style={pageBtnStyle}>1</button>
                          </li>
                          {pageRange[0] > 2 && <li className="page-item disabled"><span className="page-link" style={pageBtnStyle}>…</span></li>}
                        </>
                      )}

                      {pageRange.map((p) => (
                        <li key={p} className={`page-item ${p === safePages ? 'active' : ''}`}>
                          <button
                            className="page-link"
                            onClick={() => setCurrentPage(p)}
                            style={p === safePages ? pageActiveBtnStyle : pageBtnStyle}
                          >
                            {p}
                          </button>
                        </li>
                      ))}

                      {pageRange[pageRange.length - 1] < totalPages && (
                        <>
                          {pageRange[pageRange.length - 1] < totalPages - 1 && (
                            <li className="page-item disabled"><span className="page-link" style={pageBtnStyle}>…</span></li>
                          )}
                          <li className="page-item">
                            <button className="page-link" onClick={() => setCurrentPage(totalPages)} style={pageBtnStyle}>
                              {totalPages}
                            </button>
                          </li>
                        </>
                      )}

                      {/* Next */}
                      <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
                        <button
                          className="page-link"
                          onClick={() => setCurrentPage((p) => p + 1)}
                          style={pageBtnStyle}
                        >
                          ›
                        </button>
                      </li>
                    </ul>
                  </nav>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </>
  );
};

/* Inline styles for pagination buttons (keeps them themed) */
const pageBtnStyle = {
  background: 'rgba(255,255,255,0.05)',
  border: '1px solid rgba(72,202,228,0.25)',
  color: '#48CAE4',
  borderRadius: '6px',
  minWidth: '32px',
  textAlign: 'center',
};

const pageActiveBtnStyle = {
  ...pageBtnStyle,
  background: 'linear-gradient(135deg, #48CAE4, #0096C7)',
  color: '#fff',
  border: 'none',
  fontWeight: 700,
};

export default AdminDashboard;